import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { CITIES } from "@/data/cities";
import { AmbientLight } from "@/components/brand/AmbientLight";
import ChapterMark from "@/components/brand/ChapterMark";
import AtmosphericParticles from "@/components/brand/AtmosphericParticles";

// Florida silhouette approximated as a smooth path (SVG viewBox 100x140)
// Coordinates roughly: West coast left, peninsula curling SE.
const FLORIDA_PATH =
  "M 12 8 L 36 8 L 38 14 L 44 16 L 52 14 L 60 18 L 66 22 L 70 28 L 74 36 L 76 46 L 74 58 L 76 70 L 80 80 L 82 92 L 80 102 L 76 112 L 68 124 L 60 132 L 52 134 L 46 130 L 42 122 L 40 110 L 38 96 L 34 84 L 28 74 L 22 64 L 16 54 L 12 42 L 10 28 L 12 8 Z";

// City positions tuned to feel like a real Florida map (relative to 100x140 viewBox)
const CITY_POS = {
  orlando:    { x: 52, y: 50 },
  kissimmee:  { x: 51, y: 56 },
  tampa:      { x: 40, y: 58 },
  sarasota:   { x: 38, y: 74 },
  naples:     { x: 46, y: 104 },
};

// Connection lines between cities (in launch order)
const CONNECTIONS = [
  ["orlando", "kissimmee"],
  ["kissimmee", "tampa"],
  ["tampa", "sarasota"],
  ["sarasota", "naples"],
];

export default function FloridaCoverage() {
  const [hovered, setHovered] = useState(null);

  return (
    <section data-testid="coverage-section" className="relative py-24 sm:py-32 lg:py-52 overflow-hidden bg-midnight-gradient">
      <AmbientLight size={1100} color="blue-soft" intensity={0.4} style={{ bottom: "-10%", left: "30%" }} />
      <AmbientLight size={600} color="gold" intensity={0.3} style={{ top: "20%", right: "10%" }} />
      <AtmosphericParticles count={18} />

      <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12">
        <ChapterMark index="08" label="Florida Coverage" className="mb-14" />

        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-5">
            <h2 className="font-display-tight text-white text-[clamp(2.5rem,5.2vw,4.75rem)] leading-[0.96] tracking-[-0.045em]">
              Five launch markets.
              <br />
              <span className="gold-metallic font-display font-light">Statewide capability.</span>
            </h2>
            <div className="mt-10 h-px w-16 bg-gradient-to-r from-gold to-transparent" />
            <p className="mt-10 text-ag-text-secondary text-base lg:text-[1.05rem] leading-[1.7] font-light max-w-md">
              AG Public Adjusting operates across Florida with focus on the markets where policyholders
              most often need institutional-grade representation.
            </p>

            <div className="mt-10 space-y-2">
              {CITIES.map((c, i) => (
                <button
                  key={c.slug}
                  onMouseEnter={() => setHovered(c.slug)}
                  onMouseLeave={() => setHovered(null)}
                  className="group block w-full text-left"
                  data-testid={`coverage-trigger-${c.slug}`}
                >
                  <Link to={`/florida-coverage/${c.slug}`} className="flex items-baseline gap-4 py-2.5 transition-colors duration-300">
                    <span className="font-mono text-[0.6rem] text-gold/60 group-hover:text-gold tracking-[0.28em] w-6 transition-colors">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className={`font-display text-2xl lg:text-3xl tracking-[-0.025em] font-light transition-all duration-300 ${
                      hovered === c.slug ? "gold-metallic text-transparent translate-x-2" : "text-white/80 group-hover:text-white"
                    }`}>
                      {c.name}
                    </span>
                    <span className="ml-auto font-mono text-[0.6rem] text-ag-text-muted tracking-[0.22em] uppercase opacity-0 group-hover:opacity-100 transition-opacity">
                      {c.region}
                    </span>
                  </Link>
                </button>
              ))}
            </div>

            <div className="mt-12 pt-8 border-t border-white/8">
              <div className="font-mono text-[0.6rem] text-gold tracking-[0.28em] uppercase mb-2">Phase 2 · Expansion</div>
              <p className="text-ag-text-secondary/70 text-sm font-light leading-relaxed">
                Fort Myers · Jacksonville · Miami · West Palm Beach · Fort Lauderdale
              </p>
            </div>
          </div>

          {/* Constellation Map */}
          <div className="lg:col-span-7 relative flex items-center justify-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.94 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
              className="relative w-full max-w-[600px]"
            >
              <svg
                viewBox="0 0 100 140"
                className="w-full h-auto"
                style={{ filter: "drop-shadow(0 30px 60px rgba(0,119,255,0.25))" }}
              >
                <defs>
                  <linearGradient id="florida-fill" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="rgba(0,119,255,0.12)" />
                    <stop offset="100%" stopColor="rgba(0,13,61,0.4)" />
                  </linearGradient>
                  <linearGradient id="florida-stroke" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="rgba(198,168,107,0.5)" />
                    <stop offset="50%" stopColor="rgba(0,170,255,0.6)" />
                    <stop offset="100%" stopColor="rgba(198,168,107,0.5)" />
                  </linearGradient>
                  <radialGradient id="city-glow">
                    <stop offset="0%" stopColor="rgba(198,168,107,1)" />
                    <stop offset="50%" stopColor="rgba(198,168,107,0.5)" />
                    <stop offset="100%" stopColor="rgba(198,168,107,0)" />
                  </radialGradient>
                </defs>

                {/* Florida silhouette */}
                <motion.path
                  d={FLORIDA_PATH}
                  fill="url(#florida-fill)"
                  stroke="url(#florida-stroke)"
                  strokeWidth="0.4"
                  initial={{ pathLength: 0, opacity: 0 }}
                  whileInView={{ pathLength: 1, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 2.5, ease: [0.16, 1, 0.3, 1] }}
                />

                {/* Connection lines between cities */}
                {CONNECTIONS.map(([from, to], i) => {
                  const a = CITY_POS[from];
                  const b = CITY_POS[to];
                  const active = hovered === from || hovered === to;
                  return (
                    <line
                      key={`${from}-${to}`}
                      x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                      stroke={active ? "rgba(198,168,107,0.9)" : "rgba(198,168,107,0.4)"}
                      strokeWidth={active ? "0.4" : "0.25"}
                      strokeDasharray="0.8 0.6"
                      style={{ transition: "stroke 0.4s, stroke-width 0.4s" }}
                    />
                  );
                })}

                {/* City nodes */}
                {CITIES.map((c, i) => {
                  const pos = CITY_POS[c.slug];
                  const active = hovered === c.slug;
                  return (
                    <g
                      key={c.slug}
                      onMouseEnter={() => setHovered(c.slug)}
                      onMouseLeave={() => setHovered(null)}
                      style={{ cursor: "pointer" }}
                    >
                      {/* Pulsing outer halo */}
                      <circle cx={pos.x} cy={pos.y} r={active ? 8 : 5} fill="url(#city-glow)" style={{ transition: "r 0.4s" }} />
                      {/* Pulsing ring with CSS animation */}
                      <circle
                        cx={pos.x} cy={pos.y} r="2.5"
                        stroke="rgba(198,168,107,0.85)"
                        strokeWidth="0.2"
                        fill="none"
                        style={{
                          animation: `cityPulse${i} 2.4s ease-out infinite`,
                          transformOrigin: `${pos.x}px ${pos.y}px`,
                        }}
                      />
                      <style>{`
                        @keyframes cityPulse${i} {
                          0% { r: 2; opacity: 0.9; }
                          100% { r: ${active ? 6 : 5}; opacity: 0; }
                        }
                      `}</style>
                      {/* Core dot */}
                      <circle
                        cx={pos.x} cy={pos.y} r={active ? 1.8 : 1.4}
                        fill={active ? "#F4E4BC" : "#C6A86B"}
                        style={{ filter: "drop-shadow(0 0 5px rgba(198,168,107,1))", transition: "r 0.4s, fill 0.4s" }}
                      />
                      {/* Label */}
                      <text
                        x={pos.x + 5}
                        y={pos.y + 1.4}
                        fill={active ? "#FFFFFF" : "rgba(255,255,255,0.85)"}
                        fontSize="3.4"
                        fontFamily="Sora, sans-serif"
                        fontWeight="300"
                        style={{ transition: "fill 0.4s" }}
                      >
                        {c.name}
                      </text>
                    </g>
                  );
                })}
              </svg>

              {/* Hover detail panel */}
              <motion.div
                initial={false}
                animate={{ opacity: hovered ? 1 : 0, y: hovered ? 0 : 10 }}
                transition={{ duration: 0.3 }}
                className="absolute bottom-0 left-0 right-0 glass-strong p-5 pointer-events-none"
              >
                {hovered && (() => {
                  const c = CITIES.find((x) => x.slug === hovered);
                  if (!c) return null;
                  return (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-mono text-[0.6rem] text-gold tracking-[0.28em] uppercase">{c.region}</span>
                        <ArrowUpRight className="w-3.5 h-3.5 text-gold" />
                      </div>
                      <div className="font-display text-xl text-white font-light tracking-[-0.02em] mb-2">{c.name}</div>
                      <div className="flex flex-wrap gap-1.5">
                        {c.common_claims.slice(0, 4).map((t) => (
                          <span key={t} className="font-mono text-[0.55rem] tracking-[0.18em] uppercase text-ag-text-secondary border border-white/15 px-2 py-0.5">{t}</span>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* Hidden anchors so test agents still find city card testids */}
        <div className="sr-only">
          {CITIES.map((c) => (
            <Link key={c.slug} to={`/florida-coverage/${c.slug}`} data-testid={`city-card-${c.slug}`}>{c.name}</Link>
          ))}
        </div>
      </div>
    </section>
  );
}
